import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useOutletContext } from "react-router-dom";
import { Eye, X } from "lucide-react";
import BusinessInfoSection from "../../components/storesetting/BusinessInfoSection";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import { useStep } from "../../context/StepContext";
import { useSectionContext } from "../../context/SectionContext";
import { getNextSectionRoute } from "../../lib/getNextSectionRoute";
import { getWebsiteSettings, updateAboutSection } from "../../services/websitesetting";
import { validateImage } from "../../lib/image";
import { BusinessInfo, BusinessInfoError } from "../../types/storesetting";
import { useAuth } from "../../auth/AuthContext";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { AboutSectionFieldLimits } from "../../config/fieldlimit";
import PageHeader from "../../components/ui/PageHeader";

const AboutSection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentStep, setCurrentStep } = useStep();
  const { sections, completions, markSectionComplete } = useSectionContext();
  const toast = useToast();

  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    heading: "",
    subheading: "",
    description: "",
    image: null,
  });
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<BusinessInfoError>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [businessValid, setBusinessValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { subdomain } = useOutletContext<{ subdomain: string }>();

  useEffect(() => {
    setCurrentStep(1);
  }, [setCurrentStep]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getWebsiteSettings();
        if (data?.about_section) {
          const { heading, subheading, description, image } = data.about_section;
          setBusinessInfo(prev => ({
            ...prev,
            heading: heading || "",
            subheading: subheading || "",
            description: description || "",
          }));
          if (image && typeof image === 'string') {
            setExistingImageUrl(image.startsWith('http') ? image : `https://salonify.co.uk/user-uploads/about-section/${image}`);
          }
        }
      } catch (error: any) {
        console.error("fetchData ERROR:", error);
        console.error("fetchData Response:", error.response);
        console.error("fetchData Response Data:", error.response?.data);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // -------------------- Validation --------------------
  const validateBusinessInfo = (info: BusinessInfo) => {
    const newErrors: BusinessInfoError = {};

    if (
      !info.heading?.trim() ||
      info.heading.length < AboutSectionFieldLimits.heading.min
    )
      newErrors.heading = "Heading is required";
    else if (info.heading.length > AboutSectionFieldLimits.heading.max)
      newErrors.heading = `Heading cannot exceed ${AboutSectionFieldLimits.heading.max} characters`;

    if (
      !info.subheading?.trim() ||
      info.subheading.length < AboutSectionFieldLimits.subheading.min
    )
      newErrors.subheading = "Subheading is required";
    else if (info.subheading.length > AboutSectionFieldLimits.subheading.max)
      newErrors.subheading = `Subheading cannot exceed ${AboutSectionFieldLimits.subheading.max} characters`;

    if (
      !info.description ||
      info.description.length < AboutSectionFieldLimits.description.min
    )
      newErrors.description = `Description must be at least ${AboutSectionFieldLimits.description.min} characters`;
    else if (info.description.length > AboutSectionFieldLimits.description.max)
      newErrors.description = `Description cannot exceed ${AboutSectionFieldLimits.description.max} characters`;
    if (!(info.image instanceof File) && !existingImageUrl) {
      newErrors.image = "Image is required";
    }

    if (info.image instanceof File) {
      const validTypes = ["image/jpeg", "image/png"];
      if (!validTypes.includes(info.image.type)) {
        newErrors.image = "Unsupported image type. Only JPG and PNG allowed.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = useCallback(async () => {
    const isValid = validateBusinessInfo(businessInfo);
    setBusinessValid(isValid);

    if (!isValid) return;
    if (businessInfo.image instanceof File) {
      try {
        await validateImage(businessInfo.image);
      } catch (imgErr: any) {
        setErrors(prev => ({ ...prev, image: imgErr.message || "Invalid image" }));
        setBusinessValid(false);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      await updateAboutSection(businessInfo);
      toast.success("About section updated successfully!");

      // Mark section as completed
      markSectionComplete("aboutsection", true);

      // Get next route
      // Mark section as completed
      markSectionComplete("aboutsection", true);

      // No navigation - stay on page

    } catch (err: any) {
      console.error("handleContinue ERROR:", err);
      console.error("handleContinue Response:", err.response);

      if (err?.type === "serverValidation") {
        setErrors(err.errors);
        toast.error("Please fix the validation errors.");
      } else {
        const status = err.response?.status;
        let userMessage = "Failed to update About section.";

        if (status === 404) {
          userMessage = "About section data not found. Please refresh and try again.";
        } else if (status === 403) {
          userMessage = "You don't have permission to modify this section.";
        } else if (status === 413) {
          userMessage = "The image file is too large for the server.";
        } else if (status === 500) {
          userMessage = "Internal server error. Please try again soon.";
        } else if (err.message === "Network Error") {
          userMessage = "Network problem detected. Check your connection.";
        }

        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    businessInfo,
    sections,
    completions,
    markSectionComplete,
    navigate,
    existingImageUrl
  ]);

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isSubmitting}
          description={isSubmitting ? "Updating about section..." : "Loading about section..."}
        />
      )}
      <div className="space-y-5">

        {/* Header Card */}
        <PageHeader
          title="About Section"
          description="Tell your story and connect with your customers"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Content */}
        <div className="space-y-4">
          <BusinessInfoSection
            businessInfo={businessInfo}
            onChange={setBusinessInfo}
            onValidChange={(valid) => setBusinessValid(valid)}
            errors={errors}
            setErrors={setErrors}
            handleContinue={handleContinue}
            existingImageUrl={existingImageUrl}
            onClearExistingImage={() => setExistingImageUrl(null)}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(AboutSection);
